// App's Store
// When adding new information, check ConnectedApp to see how the data is mapped.
import * as Sentry from "@sentry/react";
import { LOGGED_OUT_STATE } from "api/auth/types";
import { compose, createStore } from "redux";
import { generic } from "store/actions";
import { ID } from "store/id";
import reducer from "./reducers";
export const DEFAULT_THEME = {
    strokeThickness: "m"
};
/**
 * Stores information for operating on multiple groups, e.g. adding child IDs
 * to multiple groups at once. Effectively a dictionary, whose key is the
 * group ID number and children are IDs.
 */
export class GroupsOperationData {
    constructor() {
        this.data = {};
        /** Maps from id number to the real Group ID. We must use these otherwise equality does not work. */
        this.groupIds = {};
        this.groupCache = {};
    }
    add(groupId, child) {
        if (!(groupId.id in this.data)) {
            this.data[groupId.id] = [];
            this.groupIds[groupId.id] = groupId;
        }
        this.data[groupId.id].push(child);
    }
    /** Do NOT mix calls to remove and add! */
    remove(groupId, child) {
        if (!(groupId.id in this.data)) {
            this.data[groupId.id] = [];
            this.groupIds[groupId.id] = groupId;
        }
        this.data[groupId.id].push(child);
        // If the group has a parent and would end up with
        // no children, remove that group too.
        const group = this.getGroup(groupId);
        if (group.items.length == this.data[groupId.id].length && group.parentGroup) {
            this.remove(group.parentGroup, groupId);
        }
    }
    getGroup(groupId) {
        if (groupId.id in this.groupCache) {
            return this.groupCache[groupId.id];
        }
        const group = appStore.getState().groups.filter(ID.matching(groupId))[0];
        this.groupCache[groupId.id] = group;
        return group;
    }
    hasData() {
        return Object.keys(this.data).length > 0;
    }
}
export const DEFAULT_CHEM_MARK_CONFIG = {
    stateOfMatterStyle: "normal",
};
var initialState = {
    zoom: 1,
    items: [],
    arrows: [],
    groups: [],
    tool: "select",
    auth: LOGGED_OUT_STATE,
    theme: Object.assign({}, DEFAULT_THEME),
    chemMark: Object.assign({}, DEFAULT_CHEM_MARK_CONFIG)
};
const sentryReduxEnhancer = Sentry.createReduxEnhancer({});
const composeEnhancers = window["__REDUX_DEVTOOLS_EXTENSION_COMPOSE__"] || compose;
export var appStore = createStore(reducer, initialState, composeEnhancers(sentryReduxEnhancer));
export default {
    // Data accessors.
    items: () => appStore.getState().items,
    arrows: () => appStore.getState().arrows,
    groups: () => appStore.getState().groups,
    // Proxy to execute actionss.
    dispatch: (action) => { appStore.dispatch(action); },
    dispatchFn: (description, fn) => { appStore.dispatch(generic(description, fn)); },
    // Helper methods.
    selectedItems: () => {
        return appStore.getState().items.filter((item) => item.selected);
    },
    selectedArrows: () => {
        return appStore.getState().arrows.filter(arrow => arrow.selected);
    },
    zoom: () => {
        return appStore.getState().zoom;
    },
    // Filters down a list of IDs to those that exist.
    filterExisting(ids) {
        return ids.filter(id => {
            switch (id.type) {
                case "arrow":
                    return appStore.getState().arrows.some(a => a.id === id);
                case "apparatus":
                    return appStore.getState().items.some(a => a.id === id);
                case "group":
                    return appStore.getState().groups.some(g => g.id === id);
            }
        });
    },
    // TODO: Clean up this as well. This was a hack to return all IDs for
    // all selecpted objects.
    selectedObjectsID: () => {
        let selectedApparatusIDs = appStore.getState().items.filter(i => i.selected).map(i => i.id);
        let selectedArrowsIDs = appStore.getState().arrows.filter(i => i.selected).map(i => i.id);
        var allIds = [];
        allIds.push(...selectedApparatusIDs);
        allIds.push(...selectedArrowsIDs);
        return allIds;
    },
    // Returns the logged in state, or null if not logged in.
    loggedInState: () => {
        const auth = appStore.getState().auth;
        if (auth.state == "logged_in") {
            return auth;
        }
        return;
    }
};
export function extractDebugInfoFromStore() {
    const state = appStore.getState();
    return {
        tier: state.tier,
        tool: state.tool,
        zoom: state.zoom,
        chemMark: state.chemMark,
        currentCloudDiagram: state.currentCloudDiagram,
        arrowsCount: state.arrows.length,
        itemsCount: state.items.length,
    };
}
